import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useSim } from "../../lib/store";
import { makeWindowTexture } from "./windowTexture";

// Resonance profiles: stiffness -> natural frequency & motion character.
const PROFILES = {
  skyscraper: { freq: 2.2, swayGain: 11, jitter: 0.0, label: "low-freq deep sway" },
  house: { freq: 24, swayGain: 1.6, jitter: 0.9, label: "sharp high-freq" },
  church: { freq: 15, swayGain: 3.2, jitter: 0.5, label: "sharp mid-freq" },
};

// Lava / ash impact scorch: chars the base color + adds an orange heat glow.
const CHAR = new THREE.Color(0.3, 0.26, 0.24);
const LAVA = new THREE.Color("#ff5518");
const _tc = new THREE.Color();
const _te = new THREE.Color();

// Progressively darkens a material's base color and pushes an orange emissive
// heat glow as the volcanic scorch level (published on window.__streetScorch) rises.
function useScorch(matRef, baseHex, emHex, emBase = 0.4) {
  const base = useMemo(() => new THREE.Color(baseHex), [baseHex]);
  const em = useMemo(() => new THREE.Color(emHex), [emHex]);
  useFrame(() => {
    const m = matRef.current;
    if (!m) return;
    const s = window.__streetScorch ?? 0;
    m.color.lerp(_tc.copy(base).lerp(CHAR, s), 0.1);
    m.emissive.lerp(_te.copy(em).lerp(LAVA, s), 0.12);
    m.emissiveIntensity = emBase + s * 1.3;
  });
}

// Jagged emissive crack overlay for a face of size w x h.
function crackGeometry(w, h, seed = 1) {
  const rand = (() => {
    let s = seed * 9301 + 49297;
    return () => ((s = (s * 9301 + 49297) % 233280), s / 233280);
  })();
  const pts = [];
  const branches = 2 + Math.floor(rand() * 2);
  for (let b = 0; b < branches; b++) {
    let x = (rand() - 0.5) * w * 0.6;
    let y = h * 0.5;
    const steps = 6;
    for (let i = 0; i < steps; i++) {
      const nx = x + (rand() - 0.5) * w * 0.25;
      const ny = y - (h / steps) * (0.7 + rand() * 0.6);
      pts.push(x, y, 0, nx, ny, 0);
      x = nx;
      y = ny;
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
  return geom;
}

function Cracks({ w, h, z, seed }) {
  const matRef = useRef();
  const geom = useMemo(() => crackGeometry(w, h, seed), [w, h, seed]);
  useFrame(() => {
    if (!matRef.current) return;
    const st = useSim.getState();
    const target = st.magnitude >= 7.5 && st.simulating ? 0.35 + st.simProgress * 0.65 : 0;
    matRef.current.opacity += (target - matRef.current.opacity) * 0.08;
  });
  return (
    <lineSegments geometry={geom} position={[0, 0, z]}>
      <lineBasicMaterial ref={matRef} color="#ff3322" transparent opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
    </lineSegments>
  );
}

// Base structure that pivots at its base and shakes per resonance profile.
function useShake(ref, type, phase) {
  useFrame((state) => {
    if (!ref.current) return;
    const p = PROFILES[type];
    const t = state.clock.elapsedTime;
    const amp = window.__streetAmp ?? 0;
    const liq = window.__streetLiq ?? 0;
    const st = useSim.getState();
    const envs = st.simulating ? Math.sin(Math.min(st.simProgress, 1) * Math.PI) * 0.7 + 0.3 : 0;
    const lean = Math.sign(Math.sin(phase * 7.3) || 1); // consistent tilt direction per building
    ref.current.rotation.z = Math.sin(t * p.freq + phase) * amp * p.swayGain + liq * 0.28 * envs * lean;
    ref.current.rotation.x = Math.cos(t * p.freq * 0.9 + phase) * amp * p.swayGain * 0.6 + liq * 0.18 * envs;
    ref.current.position.y = -(liq * 1.2 * envs); // liquefaction sink
    if (p.jitter) {
      ref.current.position.x = ref.current.userData.bx + Math.sin(t * p.freq * 1.3 + phase) * amp * p.jitter;
      ref.current.position.z = ref.current.userData.bz + Math.cos(t * p.freq * 1.1 + phase) * amp * p.jitter;
    }
  });
}

export function Skyscraper({ x, z, w = 1.3, d = 1.3, h, seed }) {
  const ref = useRef();
  const segRefs = useRef([]);
  const fissureRef = useRef();
  useShake(ref, "skyscraper", seed);

  const tex = useMemo(() => {
    const t = makeWindowTexture(6, Math.max(8, Math.round(h * 3)));
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return t;
  }, [h]);

  const N = THREE.MathUtils.clamp(Math.round(h / 1.1), 3, 6);
  const segH = h / N;

  const segData = useMemo(
    () =>
      Array.from({ length: N }, (_, i) => ({
        y0: segH * (i + 0.5),
        y: segH * (i + 0.5),
        vel: 0,
        rx: 0,
        rz: 0,
        opacity: 1,
        threshold: 0.3 + (N - 1 - i) * 0.11, // top segments break first
      })),
    [N, segH]
  );

  useFrame((_, delta) => {
    const st = useSim.getState();
    const dt = Math.min(delta, 0.05);
    const collapsing = st.magnitude >= 7.5 && st.simulating;
    for (let i = 0; i < N; i++) {
      const s = segData[i];
      const m = segRefs.current[i];
      if (!m) continue;
      if (collapsing && st.simProgress > s.threshold) {
        s.vel -= 9 * dt;
        s.y += s.vel * dt;
        s.rx += (0.6 + i * 0.2) * dt;
        s.rz += (0.4 + i * 0.15) * dt;
        s.opacity = Math.max(0, s.opacity - dt * 0.35);
        if (s.y < segH * 0.5) { s.y = segH * 0.5; s.vel = 0; }
      } else {
        // restore
        s.y += (s.y0 - s.y) * 0.1;
        s.rx += (0 - s.rx) * 0.1;
        s.rz += (0 - s.rz) * 0.1;
        s.opacity += (1 - s.opacity) * 0.1;
        s.vel = 0;
      }
      m.position.y = s.y;
      m.rotation.x = s.rx;
      m.rotation.z = s.rz;
      if (m.material) { m.material.opacity = s.opacity; m.material.transparent = s.opacity < 0.99; }
    }
    // interior floor fissure widens with elapsed quake time
    if (fissureRef.current) {
      const elapsed = st.simulating ? (performance.now() - st.simStart) / 1000 : 0;
      const width = THREE.MathUtils.clamp(elapsed / 40, 0, 1) * (st.magnitude >= 7 ? 1 : 0.4);
      fissureRef.current.scale.x = 0.02 + width;
      fissureRef.current.material.opacity = st.simulating ? 0.9 : 0;
    }
  });

  return (
    <group ref={ref} position={[x, 0, z]} userData={{ bx: x, bz: z }}>
      {segData.map((s, i) => (
        <mesh key={i} ref={(el) => (segRefs.current[i] = el)} position={[0, s.y0, 0]} castShadow>
          <boxGeometry args={[w, segH * 0.96, d]} />
          <meshStandardMaterial color="#0c1526" metalness={0.7} roughness={0.28} emissive="#ffffff" emissiveMap={tex} emissiveIntensity={0.9} transparent />
        </mesh>
      ))}
      {/* rooftop cap */}
      <mesh position={[0, h + 0.08, 0]}>
        <boxGeometry args={[w * 0.4, 0.16, d * 0.4]} />
        <meshStandardMaterial color="#1a2740" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* interior floor fissure (widens with duration) */}
      <mesh ref={fissureRef} position={[0, h * 0.42, d / 2 + 0.015]} scale={[0.02, 1, 1]}>
        <boxGeometry args={[w * 0.9, 0.05, 0.02]} />
        <meshBasicMaterial color="#ff3322" transparent opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </mesh>
      <Cracks w={w} h={h} z={d / 2 + 0.01} seed={seed} />
    </group>
  );
}

export function House({ x, z, seed }) {
  const ref = useRef();
  const bodyMat = useRef();
  const w = 1.1, d = 1.1, h = 0.9 + (seed % 3) * 0.15;
  const color = ["#3a4b6b", "#4a3b52", "#3f5560", "#524438"][seed % 4];
  useShake(ref, "house", seed);
  useScorch(bodyMat, color, "#1a1206");
  return (
    <group ref={ref} position={[x, 0, z]} userData={{ bx: x, bz: z }}>
      <mesh position={[0, h / 2, 0]} castShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial ref={bodyMat} color={color} metalness={0.15} roughness={0.8} emissive="#1a1206" emissiveIntensity={0.4} />
      </mesh>
      {/* pitched roof */}
      <mesh position={[0, h + 0.28, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[w * 0.82, 0.55, 4]} />
        <meshStandardMaterial color="#8a3b2e" metalness={0.1} roughness={0.85} />
      </mesh>
      <Cracks w={w} h={h} z={d / 2 + 0.01} seed={seed + 3} />
    </group>
  );
}

export function Church({ x, z, seed = 7 }) {
  const ref = useRef();
  const naveMat = useRef();
  const naveH = 1.3;
  useShake(ref, "church", seed);
  useScorch(naveMat, "#c9c4b4", "#241f14", 0.35);
  return (
    <group ref={ref} position={[x, 0, z]} userData={{ bx: x, bz: z }}>
      {/* nave */}
      <mesh position={[0, naveH / 2, 0]} castShadow>
        <boxGeometry args={[1.4, naveH, 2.2]} />
        <meshStandardMaterial ref={naveMat} color="#c9c4b4" metalness={0.05} roughness={0.9} emissive="#241f14" emissiveIntensity={0.35} />
      </mesh>
      {/* pitched roof over nave */}
      <mesh position={[0, naveH + 0.28, 0]} rotation={[0, 0, 0]}>
        <boxGeometry args={[1.45, 0.5, 2.25]} />
        <meshStandardMaterial color="#5b3a2a" roughness={0.85} />
      </mesh>
      {/* bell tower */}
      <mesh position={[0, 1.1, 1.35]} castShadow>
        <boxGeometry args={[0.7, 2.2, 0.7]} />
        <meshStandardMaterial color="#d6d0c0" roughness={0.9} emissive="#2a2418" emissiveIntensity={0.4} />
      </mesh>
      {/* steeple */}
      <mesh position={[0, 2.55, 1.35]}>
        <coneGeometry args={[0.5, 0.9, 4]} />
        <meshStandardMaterial color="#7a4a34" roughness={0.8} />
      </mesh>
      {/* cross */}
      <mesh position={[0, 3.15, 1.35]}>
        <boxGeometry args={[0.06, 0.34, 0.06]} />
        <meshStandardMaterial color="#ffd98a" emissive="#ffb347" emissiveIntensity={1.4} toneMapped={false} />
      </mesh>
      <Cracks w={1.4} h={naveH} z={1.12} seed={seed} />
    </group>
  );
}
